import { skipToken, useQuery } from "@apollo/client/react";
import { Collapse, Grid, useMediaQuery, useTheme } from "@mui/material";
import { ArrowLeft, BackwardBack, ChevronDown, ChevronUp } from "@pmag/icons";
// eslint-disable-next-line no-restricted-imports
import { Button, InlineError, Switch, Typography } from "@pmag/ui-components";
import { type GQLVisualSelectorModelFragment, type GQLVisualSelectorQuery, type GQLVisualSelectorQueryVariables } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRestart } from "@src/main/useRestart";
import { VehicleCard } from "@src/pages/searchResult/components/VehicleCard";
import { Breadcrumb } from "@src/pages/visualSelectorPage/components/breadcrumb/Breadcrumb";
import { CategoryCard } from "@src/pages/visualSelectorPage/components/categoryCard/CategoryCard";
import { CategoryCardSkeleton } from "@src/pages/visualSelectorPage/components/categoryCard/CategoryCardSkeleton";
import { visualSelectorQuery } from "@src/pages/visualSelectorPage/VisualSelectorPage.gql";
import { resolveColorOrderedModels, resolveQueryFilter } from "@src/pages/visualSelectorPage/VisualSelectorPage.utils";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import {
    BreadcrumbsContainer,
    BreadcrumbsWrapper,
    NavigationButton,
    ProgressContainer,
    Root,
    StyledBreadcrumbs,
    StyledGridItem,
    StyledLinearProgress,
    SwitchContainer,
    TopContainer,
} from "./VisualSelectorPage.styles";

export const dataTestId = {
    container: "sparePartsFinder.visualSelectorPage.container",
    buttonRestart: "sparePartsFinder.visualSelectorPage.button.restart",
};

export interface Breadcrumb {
    categoryId: string;
    description: string;
}

export const VisualSelectorPage: React.FunctionComponent = () => {
    const maxStep = 8;
    const theme = useTheme();
    const { restart } = useRestart();
    const { brandId, breadcrumbs, setBreadcrumbs } = useGlobalState();
    const defaultRegion = useAttributeConfig().region;
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

    const [defaultRegionModelsOnly, setDefaultRegionModelsOnly] = React.useState<boolean>(defaultRegion !== "");
    const [showBreadcrumbs, setShowBreadcrumbs] = React.useState<boolean>(!isSmallScreen);

    const categoryId = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 1].categoryId : null;

    const { data, loading, error } = useQuery<GQLVisualSelectorQuery, GQLVisualSelectorQueryVariables>(
        visualSelectorQuery,
        brandId
            ? { variables: { filter: resolveQueryFilter(categoryId === null, brandId, categoryId, defaultRegionModelsOnly, defaultRegion) } }
            : skipToken,
    );

    React.useEffect(() => {
        setShowBreadcrumbs(!isSmallScreen);
    }, [isSmallScreen]);

    const onItemSelect = (model: GQLVisualSelectorModelFragment) => {
        if (model.children.length === 1 && model.children[0].children.length > 0) {
            // skip one level if there is only one child
            setBreadcrumbs([...breadcrumbs, { categoryId: model.children[0].id, description: model.children[0].description ?? "" }]);
        } else {
            setBreadcrumbs([...breadcrumbs, { categoryId: model.id, description: model.description ?? "" }]);
        }
    };

    const step = breadcrumbs.length;
    const isLastStep = data?.models.nodes.every((model) => model.children && model.children.length == 0);
    const colorOrderedModels = resolveColorOrderedModels(data?.models.nodes, isLastStep);

    return (
        <Root data-testid={dataTestId.container}>
            <TopContainer showBorderBottom={true}>
                {!isSmallScreen && (
                    <Typography variant="heading-07">
                        <FormattedMessage id="sparePartsFinder.visualSelectorPage.title" defaultMessage="Model selector" />
                    </Typography>
                )}

                <NavigationButton variant="text" startIcon={<BackwardBack size="small" />} data-testid={dataTestId.buttonRestart} onClick={restart}>
                    <Typography variant="body-short-01">
                        <FormattedMessage id="sparePartsFinder.common.button.restart" defaultMessage="Restart" />
                    </Typography>
                </NavigationButton>
            </TopContainer>

            <ProgressContainer onClick={() => isSmallScreen && setShowBreadcrumbs(!showBreadcrumbs)}>
                <Typography variant="label-02">
                    <FormattedMessage
                        id="sparePartsFinder.visualSelectorPage.step"
                        defaultMessage="step {progress}/{max}"
                        values={{ progress: step, max: maxStep }}
                    />
                </Typography>

                <StyledLinearProgress value={(step * 100) / maxStep} variant="determinate" />

                {isSmallScreen && (showBreadcrumbs ? <ChevronUp size="small" /> : <ChevronDown size="small" />)}
            </ProgressContainer>

            <BreadcrumbsContainer>
                <Collapse in={showBreadcrumbs}>
                    <BreadcrumbsWrapper>
                        <StyledBreadcrumbs variant="default">
                            {breadcrumbs.map((breadcrumb, index) =>
                                index <= step ? (
                                    <Breadcrumb
                                        key={`breadcrumb${index}`}
                                        onClick={() => {
                                            setBreadcrumbs(breadcrumbs.slice(0, index + 1));
                                        }}
                                    >
                                        {breadcrumb.description}
                                    </Breadcrumb>
                                ) : null,
                            )}
                        </StyledBreadcrumbs>
                    </BreadcrumbsWrapper>
                </Collapse>

                {isLargeScreen && isLastStep && defaultRegion !== "" && (
                    <Switch
                        checked={defaultRegionModelsOnly}
                        onChange={(_, value) => setDefaultRegionModelsOnly(value)}
                        label={
                            <FormattedMessage
                                id="sparePartsFinder.common.defaultRegionModelsOnly"
                                defaultMessage="{region} models only"
                                values={{ region: defaultRegion }}
                            />
                        }
                    />
                )}
            </BreadcrumbsContainer>

            {!isLargeScreen && (isLastStep || (isSmallScreen && step > 1)) && (
                <SwitchContainer>
                    {isSmallScreen && step > 1 && (
                        <Button
                            variant="text"
                            startIcon={<ArrowLeft size="small" />}
                            onClick={() => {
                                setBreadcrumbs(breadcrumbs.slice(0, breadcrumbs.length - 1));
                            }}
                        >
                            <FormattedMessage id="sparePartsFinder.visualSelectorPage.previousStep" defaultMessage="Previous step" />
                        </Button>
                    )}

                    {isLastStep && defaultRegion !== "" && (
                        <Switch
                            checked={defaultRegionModelsOnly}
                            onChange={(_, value) => setDefaultRegionModelsOnly(value)}
                            label={
                                <FormattedMessage
                                    id="sparePartsFinder.common.defaultRegionModelsOnly"
                                    defaultMessage="{region} models only"
                                    values={{ region: defaultRegion }}
                                />
                            }
                        />
                    )}
                </SwitchContainer>
            )}

            {error && <InlineError />}

            <Grid
                container
                columns={{
                    lg: 12,
                    md: 12,
                    sm: 8,
                    xl: 12,
                    xs: 4,
                }}
                spacing={isSmallScreen ? 0 : 3}
            >
                {loading
                    ? Array(3)
                          .fill(null)
                          .map((_, index) => (
                              <StyledGridItem item xs={8} sm={4} md={4} lg={3} xl={3} key={`selector-card-${index}`}>
                                  <CategoryCardSkeleton />
                              </StyledGridItem>
                          ))
                    : !isLastStep &&
                      data?.models.nodes.map((model) =>
                          model.description ? (
                              <StyledGridItem item xs={8} sm={4} md={4} lg={3} xl={3} key={model.id}>
                                  <CategoryCard onClick={() => onItemSelect(model)} description={model.description} count={model.children.length} />
                              </StyledGridItem>
                          ) : undefined,
                      )}
            </Grid>

            {colorOrderedModels.map((model) => (
                <VehicleCard key={model.id} model={model} />
            ))}

            {isLastStep && colorOrderedModels.length === 0 && (
                <Typography variant="label-02">
                    <FormattedMessage
                        id="sparePartsFinder.visualSelectorPage.noModelsForRegion"
                        defaultMessage="No module for the selected culture {region} available."
                        values={{ region: defaultRegion }}
                    />
                </Typography>
            )}
        </Root>
    );
};
